import axios from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Card, Col, Modal, Row, Table } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';
import Pagination from '../../Pagination';
import DashboardLayout from './DashboardLayout ';
import GlobalHRSettings from './GlobalHRSettings';
import { SiImessage } from 'react-icons/si';
import { MdDelete, MdEdit } from 'react-icons/md';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane } from '@fortawesome/free-solid-svg-icons';
import SockJS from 'sockjs-client';
import { Client } from '@stomp/stompjs';
import Swal from 'sweetalert2';
import '../ChatComponent.css';
import { format, isToday, isYesterday } from 'date-fns';

const People = () => {
    const BASE_API_URL = process.env.REACT_APP_API_URL;
    const location = useLocation();
    const userName = location.state?.userName;
    const userEmail = location.state?.userEmail;
    const userId = location.state?.userId;

    const [people, setPeople] = useState([]);
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(6);
    const [totalPages, setTotalPages] = useState(0);
    const [sortedColumn, setSortedColumn] = useState(null);
    const [sortOrder, setSortOrder] = useState('asc');
    const isLastPage = page === totalPages - 1;
    const isPageSizeDisabled = isLastPage;

    const fetchHRData = useCallback(async () => {
        if (!user) return;
        try {
            setLoading(true);
            const params = {
                userEmail: userEmail,
                page,
                size: pageSize,
                sortBy: sortedColumn,
                sortOrder,
            };
            const response = await axios.get(`${BASE_API_URL}/getHrEachCompany`, { params });
            setPeople(response.data.content);
            setTotalPages(response.data.totalPages);
        } catch (error) {
            console.error("Error fetching HR data:", error);
        } finally {
            setLoading(false);
        }
    }, [user, page, pageSize, sortedColumn, sortOrder]);

    useEffect(() => {
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
            setUser(JSON.parse(storedUser));
        } else if (userName && userEmail) {
            setUser({ userName, userEmail });
        }
    }, [location.state]);

    useEffect(() => {
        fetchHRData();
    }, [fetchHRData]);

    const handleSort = (column) => {
        let order = 'asc';
        if (sortedColumn === column) {
            order = sortOrder === 'asc' ? 'desc' : 'asc';
        }
        setSortedColumn(column);
        setSortOrder(order);
    };

    const handlePageClick = (data) => {
        setPage(data.selected);
    };

    const handlePageSizeChange = (e) => {
        setPageSize(parseInt(e.target.value));
        setPage(0);
    };

    // Chat logic
    const [isChatOpen, setIsChatOpen] = useState(false);
    const [chatWithUser, setChatWithUser] = useState(null);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [editMessageId, setEditMessageId] = useState(null);
    const [isSending, setIsSending] = useState(false);
    const stompClientRef = useRef(null);
    const chatBoxRef = useRef(null);

    const fetchMessages = async (fromId, toId) => {
        try {
            const res = await axios.get(`${BASE_API_URL}/messagesByHRIds`, {
                params: { senderId: fromId, recipientId: toId }
            });
            setMessages(res.data || []);
        } catch (error) {
            console.error('Failed to fetch chat messages', error);
        }
    };

    const openChat = async (person) => {
        setChatWithUser(person);
        setIsChatOpen(true);

        // Fetch existing messages between user and the selected person
        await fetchMessages(userId, person.userId);

        // Create SockJS client
        const socket = new SockJS(`${BASE_API_URL}/ws`);
        const client = new Client({
            webSocketFactory: () => socket,
            onConnect: () => {
                // Subscribe to the topic
                client.subscribe('/topic/app', (messageOutput) => {
                    const message = JSON.parse(messageOutput.body);

                    setMessages((prevMessages) => {
                        // Check if message already exists (for update)
                        const index = prevMessages.findIndex(msg => msg.hrChatId === message.hrChatId);

                        if (index > -1) {
                            // Update existing message
                            const updatedMessages = [...prevMessages];
                            updatedMessages[index] = message;
                            return updatedMessages;
                        } else {
                            // New message, append to list
                            return [...prevMessages, message];
                        }
                    });
                });
            },
            onStompError: (error) => {
                console.error('WebSocket error:', error);
            }
        });

        client.activate();
        stompClientRef.current = client;
    };


    const closeChat = () => {
        setIsChatOpen(false);
        if (stompClientRef.current) {
            stompClientRef.current.deactivate();
        }
    };

    const sendMessage = async () => {
        if (!newMessage.trim()) return;

        const stompClient = stompClientRef.current;

        if (!stompClient || !stompClient.connected) {
            return;
        }

        const messagePayload = {
            senderId: userId,
            recipientId: chatWithUser.userId,
            messageContent: newMessage,
            createdAt: new Date().toISOString(),
        };

        stompClientRef.current.publish({
            destination: '/app/hr-chat',
            body: JSON.stringify(messagePayload)
        });

        setNewMessage('');
    };


    const formatDate = (timestamp) => {
        if (!timestamp) return 'Invalid Date';
        const date = new Date(timestamp);
        if (isNaN(date.getTime())) return 'Invalid Date';
        if (isToday(date)) return 'Today';
        if (isYesterday(date)) return 'Yesterday';
        return format(date, 'PPPP');
    };

    const formatTime = (timestamp) => {
        if (!timestamp) return '';
        const date = new Date(timestamp);
        if (isNaN(date.getTime())) return '';
        return format(date, 'hh:mm a');
    };


    const handleDelete = async (msgId) => {
        const confirm = await Swal.fire({
            title: 'Are you sure?',
            text: "Delete this message?",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, delete it!',
        });

        if (confirm.isConfirmed) {
            await axios.delete(`${BASE_API_URL}/deleteChatMsg`, { params: { chatId: msgId } });
            setMessages(messages.filter(msg => msg.chatId !== msgId));
        }
    };

    return (
        <DashboardLayout>
            <div className="main-content">
                <Row className="d-flex justify-content-between align-items-center mb-3">
                    <Col xs="auto"><h2>HR Details</h2></Col>
                    <Col xs="auto"><GlobalHRSettings /></Col>
                </Row>

                {loading ? (
                    <div className="text-center m-5">Loading...</div>
                ) : (
                    <Card body>
                        <div className="table-wrapper">
                            <Table hover responsive>
                                <thead>
                                    <tr>
                                        <th onClick={() => handleSort('userName')} style={{ cursor: 'pointer' }}>
                                            HR Name ↑↓
                                        </th>
                                        <th onClick={() => handleSort('userEmail')} style={{ cursor: 'pointer' }}>
                                            Email ↑↓
                                        </th>
                                        <th>Company Name</th>
                                        <th>Chat</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {people.map(person => (
                                        <tr key={person.userId}>
                                            <td>{person.userName}</td>
                                            <td><a href={`mailto:${person.userEmail}`}>{person.userEmail}</a></td>
                                            <td>{person.companyName}</td>
                                            <td>
                                                <SiImessage
                                                    size={25}
                                                    onClick={person.userEmail !== userEmail ? () => openChat(person) : undefined}
                                                    style={{
                                                        color: person.userEmail !== userEmail ? 'green' : 'gray',
                                                        cursor: person.userEmail !== userEmail ? 'pointer' : 'not-allowed',
                                                        opacity: person.userEmail !== userEmail ? 1 : 0.5
                                                    }}
                                                />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                        <Pagination
                            page={page}
                            pageSize={pageSize}
                            totalPages={totalPages}
                            handlePageSizeChange={handlePageSizeChange}
                            isPageSizeDisabled={isPageSizeDisabled}
                            handlePageClick={handlePageClick}
                        />
                    </Card>
                )}
            </div>

            {/* Chat Modal */}
            <Modal show={isChatOpen} onHide={closeChat} size="md">
                <Modal.Header closeButton>
                    <Modal.Title>Chat with {chatWithUser?.userName}</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    <div ref={chatBoxRef}>
                        {messages.map((msg, idx) => {
                            const isSender = msg.senderId === userId;
                            const messageContent = msg.messageContent;
                            const senderName = isSender ? 'You' : chatWithUser.userName;
                            const timestamp = msg.createdAt;

                            return (
                                <div key={idx} className={`mb-2 ${isSender ? 'text-end' : 'text-start'}`}>
                                    <div className="p-2 bg-light d-inline-block rounded">
                                        <strong>{senderName}:</strong> {messageContent}
                                        <div className="text-muted small">
                                            {formatDate(timestamp)} at {formatTime(timestamp)}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    <textarea
                        className="form-control mt-3"
                        rows={2}
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                                sendMessage();
                                e.preventDefault();
                            }
                        }}
                        placeholder="Type a message..."
                    />
                    <div className="text-end mt-2">
                        <Button variant="primary" onClick={sendMessage} disabled={!newMessage.trim()}>
                            <FontAwesomeIcon icon={faPaperPlane} /> Send
                        </Button>
                    </div>
                </Modal.Body>
            </Modal>
        </DashboardLayout>
    );
};

export default People;
